import {
    DataTable,
    AuthContext,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderRefundRequestsRequest,
    PatientBadge,
    ProviderCollectionRefundRequest,
    ProviderThinPatient,
    renderAmount,
    renderDateTime,
    ProviderClaim,
    FilterMethod,
    ToastContext,
    ProviderThinUser,
    ListProviderDocumentsRequest,
    ProviderDisplayDocumentType
} from "@enter/core";
import {DataTableCard, PageHeader, PageLayout, PageLayoutRow, ProviderMessagingContext} from 'components';
import { DialogContext, IDialogContextProps } from 'services';
import * as S from './overpayments.styles';
import {IInjectedRootStore, IObservedRootStore, parseSortBy, RootStore} from "stores";
import {inject, observer} from "mobx-react";
import React, { useContext } from 'react';
import {NotesDialog} from "../../../components/notes-dialog/notes-dialog";
import {SubPageLayout} from "./overpayments.styles";

interface IOverpaymentsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IOverpaymentsState {
    loading: boolean;
    options: ITableFetchOptions | undefined;
}

const filterSchemas: IFilterSchema<keyof ListProviderRefundRequestsRequest>[] = [
    {
        label: 'External Patient Id',
        filterId: 'filterExternalPatientId',
        columnId: 'patient',
        filterType: 'String',
    }
];

const columnsBase: ITableColumn<ProviderCollectionRefundRequest>[] = [
    {
        id: 'Id',
        Header: 'Id',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'RefundAmount',
        Header: 'Refund Amount',
        accessor: 'refundAmount',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderAmount(cell.value)}</S.Cell>,
    },
    {
        id: 'Patient',
        Header: 'Patient',
        accessor: 'patient',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{
            cell.value ?<PatientBadge
                id={(cell.value as ProviderThinPatient).patientId}
                first={(cell.value as ProviderThinPatient).first}
                last={(cell.value as ProviderThinPatient).last}
                suffix={(cell.value as ProviderThinPatient).suffix}
                tooltip="View Patient Statement"
                path={`/patients/${(cell.value as ProviderThinPatient).patientId}/statement`} /> : <></>
        }</S.Cell>,
    },
    {
        id: 'ApproveUser',
        Header: 'Approved By',
        accessor: 'approveUser',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value ? (cell.value as ProviderThinUser).first : ''} {cell.value ? (cell.value as ProviderThinUser).last : ''}</S.Cell>,
    },
    {
        id: 'ApproveDate',
        Header: 'Approved',
        accessor: 'approveDate',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
    },
    {
        id: 'CreateDate',
        Header: 'Created',
        accessor: 'created',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
    },
];



class OverpaymentsApprovedPage extends React.Component<IOverpaymentsProps, IOverpaymentsState> {
    private presenter: DataTablePresenter<ProviderCollectionRefundRequest>;

    private columns: ITableColumn<ProviderCollectionRefundRequest>[] = [];

    constructor(props: IOverpaymentsProps) {
        super(props);
        this.state = {
            loading: false,
            options: undefined,
        };
        const rootStore: RootStore = props.rootStore;
        const columns: ITableColumn<ProviderCollectionRefundRequest>[] = [
            ...columnsBase,
            {
                id: 'Action',
                Header: '',
                disableSortBy: true,
                Cell: ({ row }): React.ReactElement => {
                    const messagingContext = useContext(ProviderMessagingContext);
                    const dialogContext = useContext(DialogContext);
                    const toastContext = useContext(ToastContext);

                    const collectionRefundRequest: ProviderCollectionRefundRequest|undefined = rootStore.overpaymentStore.findCollectionRefundRequest(row.original.id ? row.original.id : '');

                    const handleUnapprove = async () => {
                        if(collectionRefundRequest?.id) {
                            await rootStore.overpaymentStore.unapproveRequest(collectionRefundRequest.id);
                            toastContext.notify?.('Refund has been unapproved', 'right');
                            window.location.reload();
                        }
                    }

                    const viewNotes = async (notes?: string) => {
                        const handleClose = (): void => {
                            dialogContext.hideDialog();
                        };

                        const handleSubmit = (notes?: string): Promise<string|undefined> => {
                            dialogContext.hideDialog();
                            return Promise.resolve(undefined);
                        }

                        dialogContext.showDialog({
                            props: {
                                onClose: handleClose,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Approval Notes',
                            },
                            Component: <NotesDialog
                                notes={collectionRefundRequest?.approveNotes}
                                readOnly={true}
                                onClose={handleClose}
                                onSubmit={handleSubmit}
                            />,
                        });
                    };

                    const handleMessage = () => {

                        const patient = collectionRefundRequest?.patient;
                        const refundAmount = collectionRefundRequest?.refundAmount;
                        const guarantor = collectionRefundRequest?.businessGuarantor;

                        let message = '';

                        if(patient) {
                            message = "Patient: " + patient.first + " " + patient.last + " - (" + patient.dob + ")\n"
                            message+= "Patient external id: " + patient.externalId + "\n";
                        } else if(guarantor) {
                            message = "Guarantor: " + guarantor.organization + " ("  + guarantor.externalOrganizationId + ")\n"
                        }

                        if(refundAmount) {
                            message+= "Refund: " + refundAmount.amount + "\n";
                        }

                        message+= "\nQuestion:\n";


                        messagingContext.openPanel?.({
                            isComposeOpen: true,
                            defaultSubject: 'Question About Refund',
                            defaultBody: message
                        });
                    };

                    return (
                        <S.ActionButton
                            id={row.original.id as string}
                            actions={[
                                {
                                    label: 'Unapprove',
                                    onClick: handleUnapprove,
                                },
                                {
                                    label: 'Create a Message',
                                    onClick: handleMessage,
                                },
                                {
                                    label: 'View Notes',
                                    onClick: viewNotes
                                }
                            ]}
                        />
                    );
                },
            },
        ];

        this.presenter = new DataTablePresenter<ProviderCollectionRefundRequest>({
            name: 'overpayments',
            data: [],
            columns,
            filterSchemas,
        });

        this.columns = columns;
    }


    public componentDidMount(): void {
        document.title = 'Provider Dashboard - Overpayments';
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('overpayments');
    }

    public render() {
        return (
            <S.SubPageLayout>
                <DataTableCard size="large">
                    <DataTable presenter={this.presenter} isLoading={this.state.loading} onFetch={this.onFetch} />
                </DataTableCard>
            </S.SubPageLayout>
        );
    }
    /*
    <PageLayout>
                <PageHeader title="Approved" hideDateRange={true} />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable presenter={this.presenter} isLoading={this.state.loading} onFetch={this.onFetch} />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
     */



    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }


    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        try {
            this.setState({
                options: fetchOptions,
                loading: true,
            });
            this.authContext.cancelGroup('overpayments');
            this.authContext.setGroup('overpayments');

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const sortAndOrder = parseSortBy(sortBy, this.columns);
            const listing = await this.props.rootStore.overpaymentStore.loadOverpaymentRefundListing({
                page: pageIndex,
                pageSize,
                ...sortAndOrder,
                filters: [
                    {filterId: 'filterApproveDate', filterMethod: FilterMethod.NEMPTY, filterType: 'String', value: ''},
                    {filterId: 'filterCollectionSubmissionId', filterMethod: FilterMethod.EMPTY, filterType: 'String', value: ''},
                    ...validFilters
                ],
            });
            this.presenter.onDataUpdate(listing.items || []);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({
                loading: false,
            });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };

}


export const OverpaymentsApproved: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return <OverpaymentsApprovedPage authContext={authContext} dialogContext={dialogContext} rootStore={rootStore} />;
    }),
);